import React, { Suspense } from "react";
import RcTooltip from "rc-tooltip";
import moment from "moment";
import { getChartData, getLeftPanelTreeData } from "../../utils/verticalEventHandling";
import { normalizeKpiSeries } from "../../realtime/kpiChartData";
import Load from "../../components/loadAction";
import {
  AiInsightPanelParagraphs,
  AiInsightPanelRecommendation,
  fetchIncidentAiInsights,
  getVerticalAiInsightsRequest
} from "./incidentAiInsightContent";
import { ExternalLink, X, ChevronRight, Database, Layers, Clock, TriangleAlert, Blocks } from "lucide-react";

const renderBreadcrumbText = (text) => {
  if (!text) return null;
  if (text.length > 30) {
    const truncated = text.substring(0, 30) + "...";
    return (
      <RcTooltip placement="top" overlay={<span>{text}</span>}>
        <span className="cursor-pointer">{truncated}</span>
      </RcTooltip>
    );
  }
  return <span>{text}</span>;
};

const ChartComponent = React.lazy(() => import("../../realtime/chartcomponent"));

const TIME_RANGES = ["1D", "5D", "1M", "3M", "6M"];
const RANGE_OFFSETS = {
  "1D": [0, "days"],
  "5D": [5, "days"],
  "1M": [1, "months"],
  "3M": [3, "months"],
  "6M": [6, "months"]
};
const CHART_UNAVAILABLE = "Chart data is not available for this incident.";
const CHART_EMPTY = "No data available for the selected range.";
const CHART_FAILED = "Failed to load chart data.";

// Same windows as the realtime Vertical page: with no range picked it loads the last 30 days
// (selectedStartDate / selectedEndDate), and the range buttons end today (handleDateRange).
export const getTimeRangeDates = (range) => {
  if (!RANGE_OFFSETS[range]) {
    return {
      startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
      endDate: moment().utc().endOf("day").format("YYYY-MM-DD")
    };
  }
  const endDate = moment().format("YYYY-MM-DD");
  const [amount, unit] = RANGE_OFFSETS[range];
  return { startDate: moment(endDate).subtract(amount, unit).format("YYYY-MM-DD"), endDate };
};

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const getIncidentAttributeIds = (incident) => {
  const map = incident?.attribute_names ?? incident?.attributeNames;
  if (!map || typeof map !== "object" || Array.isArray(map)) return [];
  return Object.keys(map).map(Number).filter(Number.isFinite);
};

// `raw` is handed to the realtime ChartComponent as `realdata`, exactly as the realtime Vertical page does.
const toModalSeries = (key, series, fallbackName) => {
  const values = Array.isArray(series.value) ? series.value : Array.isArray(series.values) ? series.values : [];
  const errors =
    series.metric_threshold_error_status || series.kpi_threshold_error_status || series.metrics_threshold_error_status || [];
  return {
    key,
    name: series.name || fallbackName || key,
    time: Array.isArray(series.time) ? series.time : [],
    values,
    hasIssue: Array.isArray(errors) && errors.some(Boolean),
    raw: series
  };
};

const isNumeric = (value) => isPresent(value) && Number.isFinite(Number(value));

const hasPoints = (series) => series.time.length > 0 && series.values.some(isNumeric);

const issuesFirst = (list) => list.sort((a, b) => Number(b.hasIssue) - Number(a.hasIssue));

// monitor/realtime chart keys: `${attributeId}_${metricId}_attr` or `${datasetId}_${metricId}_dt`.
// Like the realtime page (filterChartDataByDataset), a dataset view also shows the returned attribute series.
export const getModalChartSeries = (chartData, datasetId, attributeId, attributeNames = {}) => {
  const entries = Object.entries(chartData || {}).filter(([, series]) => series && typeof series === "object");
  const isDatasetKey = (key) => key.startsWith(`${datasetId}_`) && key.endsWith("_dt") && !key.endsWith("_kpi_dt");
  const pick = (predicate, labelAttribute = false) =>
    entries
      .filter(([key]) => predicate(key))
      .map(([key, series]) => {
        const modalSeries = toModalSeries(key, series);
        if (labelAttribute) {
          const attrId = key.split("_")[0];
          const attrName = attributeNames[attrId] ?? attributeNames[Number(attrId)];
          if (attrName) modalSeries.name = `${attrName} · ${modalSeries.name}`;
        }
        return modalSeries;
      })
      .filter(hasPoints);

  if (isPresent(attributeId)) {
    const own = pick((key) => key.startsWith(`${attributeId}_`) && key.endsWith("_attr"));
    if (own.length) return issuesFirst(own);
    const anyAttribute = pick((key) => key.endsWith("_attr"), true);
    return issuesFirst(anyAttribute.length ? anyAttribute : pick(isDatasetKey));
  }
  return [...issuesFirst(pick(isDatasetKey)), ...issuesFirst(pick((key) => key.endsWith("_attr"), true))];
};

export const getModalKpiSeries = (chartResponse, kpiId, kpiName) => {
  const kpiDetails = chartResponse?.kpi_details || {};
  const chartData = chartResponse?.chart_data || chartResponse?.chart_details || {};
  const normalizedName = String(kpiName || "").trim().toLowerCase();
  const raw =
    kpiDetails[kpiId] ??
    kpiDetails[String(kpiId)] ??
    chartData[`${kpiId}_kpi_dt`] ??
    Object.entries(chartData).find(([key]) => key.startsWith(`${kpiId}_`) && key.endsWith("_kpi_dt"))?.[1] ??
    (normalizedName
      ? Object.values(kpiDetails).find((value) => String(value?.name || "").trim().toLowerCase() === normalizedName)
      : undefined);
  if (!raw || typeof raw !== "object") return [];
  // Same shaping as the realtime page's buildKpiChartData.
  return [toModalSeries(`${kpiId}_kpi_dt`, normalizeKpiSeries(raw, kpiName, kpiId), kpiName)].filter(hasPoints);
};

const readStoredId = (storage, key) => {
  try {
    return window[storage]?.getItem(key) || null;
  } catch (error) {
    return null;
  }
};

// Same lookup as the realtime Vertical KPI view: the KPI entry in /monitor/verticalization `kpis` holds its dataset context.
export const findKpiSource = (treeData, kpiId, subjectAreaId) => {
  const kpis = treeData?.kpis || {};
  const lists = [kpis[subjectAreaId], ...Object.values(kpis)].filter(Array.isArray);
  for (const list of lists) {
    const found = list.find((kpi) => Number(kpi?.value) === Number(kpiId));
    if (found) return found;
  }
  return null;
};

// Full (unfiltered) tree: kpis keyed by subject area id, subject_areas keyed by business area id.
export const findKpiLocation = (treeData, kpiId) => {
  const kpis = treeData?.kpis || {};
  const subjectAreaId = Object.keys(kpis).find(
    (key) => Array.isArray(kpis[key]) && kpis[key].some((kpi) => Number(kpi?.value) === Number(kpiId))
  );
  if (subjectAreaId === undefined) return null;
  const subjectAreas = treeData?.subject_areas || {};
  const businessAreaId = Object.keys(subjectAreas).find(
    (key) => Array.isArray(subjectAreas[key]) && subjectAreas[key].some((sa) => String(sa?.value) === String(subjectAreaId))
  );
  return { subjectAreaId: Number(subjectAreaId), businessAreaId: businessAreaId !== undefined ? Number(businessAreaId) : null };
};

const normalizeLabel = (value) => String(value ?? "").trim().toLowerCase();

// [{ businessAreaId, subjectAreaId, label }] from the tree's subject_areas (keyed by business area id).
export const listSubjectAreas = (treeData) =>
  Object.entries(treeData?.subject_areas || {}).flatMap(([businessAreaId, list]) =>
    (Array.isArray(list) ? list : [])
      .filter((sa) => isPresent(sa?.value))
      .map((sa) => ({ businessAreaId: Number(businessAreaId), subjectAreaId: Number(sa.value), label: sa.label }))
  );

// Hero incidents may carry only the subject area name; prefer a match inside the named business area.
export const findSubjectAreaByName = (treeData, subjectAreaName, businessAreaName) => {
  const wanted = normalizeLabel(subjectAreaName);
  if (!wanted) return null;
  const matches = listSubjectAreas(treeData).filter((sa) => normalizeLabel(sa.label) === wanted);
  const businessIds = (treeData?.business_areas || [])
    .filter((ba) => businessAreaName && normalizeLabel(ba?.label) === normalizeLabel(businessAreaName))
    .map((ba) => Number(ba.value));
  const match = matches.find((sa) => businessIds.includes(sa.businessAreaId)) || matches[0];
  return match ? { subjectAreaId: match.subjectAreaId, businessAreaId: match.businessAreaId } : null;
};

// The incident's own business area first, then the one matching its business area name, then the rest.
export const orderBusinessAreas = (businessAreas, businessAreaId, businessAreaName) => {
  const list = (Array.isArray(businessAreas) ? businessAreas : []).filter((ba) => isPresent(ba?.value));
  const rank = (ba) => {
    if (isPresent(businessAreaId) && String(ba.value) === String(businessAreaId)) return 0;
    if (businessAreaName && normalizeLabel(ba.label) === normalizeLabel(businessAreaName)) return 1;
    return 2;
  };
  return list.map((ba, index) => ({ ba, index })).sort((a, b) => rank(a.ba) - rank(b.ba) || a.index - b.index).map(({ ba }) => ba);
};

const MAX_SUBJECT_AREA_SCAN = 30;

export const getKpiDatasetContext = (kpiSource) => {
  const integrationId = kpiSource?.integrations?.[0]?.value || kpiSource?.integration_ids?.[0] || null;
  let datasetId = integrationId ? kpiSource?.datasets?.[integrationId]?.[0]?.value : null;
  if (!isPresent(datasetId) && kpiSource?.datasets) {
    const firstList = Object.values(kpiSource.datasets).find((list) => Array.isArray(list) && list.length > 0);
    datasetId = firstList?.[0]?.value ?? null;
  }
  return {
    integrationId,
    datasetId,
    attributeIds: getKpiAttributeIds(kpiSource, datasetId, false),
    found: Boolean(kpiSource),
    kpiSource
  };
};

// The realtime Dataset view sorts attribute ids; its KPI view keeps the verticalization order.
export const getKpiAttributeIds = (kpiSource, datasetId, sorted = true) => {
  if (!isPresent(datasetId)) return [];
  const list = kpiSource?.attributes?.[datasetId] ?? kpiSource?.attributes?.[Number(datasetId)] ?? [];
  const ids = (Array.isArray(list) ? list : [])
    .map((attr) => attr?.value)
    .filter((value) => typeof value === "number");
  return sorted ? ids.sort((a, b) => a - b) : ids;
};

// Start of the dataset's history (first_processed_time), but never less than a year back.
export const getHistoryStartDate = (responseBody) => {
  const yearAgo = moment().subtract(1, "year").startOf("day");
  const firstProcessed = moment(responseBody?.first_processed_time || responseBody?.start_date || null);
  const start = firstProcessed.isValid() && firstProcessed.isBefore(yearAgo) ? firstProcessed : yearAgo;
  return start.format("YYYY-MM-DD");
};

export const describeChartAttempt = (params, response, kpi) => {
  const kpiIds = Object.keys(response?.response?.kpi_details || {});
  const parts = [
    kpi ? `KPI ${kpi.kpiId}` : null,
    `subject area ${params.subject_area_id ?? "-"}`,
    `env ${params.env_id ?? "-"}`,
    `dataset ${params.data_set_id ?? "-"}`,
    `${params.start_date || "-"} → ${params.end_date || "-"}`,
    `response ${response?.code ?? "none"}${response?.message ? ` (${response.message})` : ""}`,
    `KPIs returned: ${kpiIds.length ? kpiIds.join(", ") : "none"}`
  ];
  return parts.filter(Boolean).join(" · ");
};

// The realtime chart widget is styled through these variables (dark values live under `html.dark`);
// scoping them here gives the modal a dark chart whatever the app theme is.
const getDarkChartVars = (background) => ({
  "--layout-bg": background,
  "--hover-bg": "rgba(255, 255, 255, 0.08)",
  "--dropdown-menu-bg": "#1E1A45",
  "--realtime-toolbar-icon-color": "rgba(255, 255, 255, 0.75)",
  "--realtime-chart-btn-grey-color": "rgba(255, 255, 255, 0.85)",
  "--realtime-chart-btn-grey-border": "rgba(255, 255, 255, 0.18)",
  "--realtime-chart-btn-grey-icon": "rgba(255, 255, 255, 0.55)",
  "--realtime-chart-filter-border": "rgba(255, 255, 255, 0.18)",
  "--realtime-chart-filter-bg": "rgba(255, 255, 255, 0.04)",
  "--realtime-chart-dropdown-shadow": "0 4px 12px rgba(0, 0, 0, 0.5)",
  "--realtime-chart-separator-bg": "rgba(255, 255, 255, 0.12)",
  "--realtime-chart-stat-caption-color": "rgba(255, 255, 255, 0.55)",
  "--realtime-chart-stat-value-color": "rgba(255, 255, 255, 0.9)",
  "--btn-outline-grey-color": "rgba(255, 255, 255, 0.85)",
  "--btn-outline-grey-border": "rgba(255, 255, 255, 0.18)",
  "--btn-outline-grey-bg": "transparent",
  "--btn-outline-grey-hover-bg": "rgba(255, 255, 255, 0.08)"
});

const logModal = (label, payload) => {
  // eslint-disable-next-line no-console
  console.info(`[VerticalDashboardModal] ${label}`, payload);
};

class verticalDashboardModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTimeRange: null,
      liked: null,
      selectedChartType: "Area",
      chartLoading: false,
      chartError: null,
      chartDiagnostics: null,
      chartSeries: [],
      chartWindow: null,
      selectedSeriesKey: null,
      insights: null,
      insightsLoading: false,
      insightsError: null
    };
    this.chartRequestId = 0;
    this.insightsRequestId = 0;
    this.kpiContextCache = {};
  }

  handleChartTypeChange = (type) => {
    this.setState({ selectedChartType: type });
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadChartData();
      this.loadInsights();
    }
  }

  // Same vertical ai_insights call (and session cache) as the incident's AI Insights popover.
  loadInsights = async () => {
    const requestId = ++this.insightsRequestId;
    const { incidentData, teamId, userId } = this.props;
    const request = getVerticalAiInsightsRequest(incidentData, teamId, userId);
    this.setState({ insights: null, insightsLoading: true, insightsError: null });
    const result = await fetchIncidentAiInsights(request);
    if (requestId !== this.insightsRequestId) return;
    this.setState({ insights: result.data || null, insightsLoading: false, insightsError: result.data ? null : result.error });
  };

  componentDidUpdate(prevProps) {
    const opened = this.props.isOpen && !prevProps.isOpen;
    const incidentChanged = this.props.isOpen && prevProps.incidentData !== this.props.incidentData;
    const envChanged = this.props.isOpen && prevProps.envId !== this.props.envId;
    const targetChanged = this.props.isOpen && prevProps.targetType !== this.props.targetType;
    if (opened || incidentChanged || envChanged || targetChanged) {
      this.setState({ selectedSeriesKey: null, selectedTimeRange: null, chartSeries: [], chartWindow: null }, this.loadChartData);
    }
    if (opened || incidentChanged) {
      this.setState({ liked: null });
      this.loadInsights();
    }
  }

  componentWillUnmount() {
    this.chartRequestId += 1;
    this.insightsRequestId += 1;
  }

  getKpiIds = () => {
    const { incidentData } = this.props;
    const kpiId = incidentData?.kpi_id;
    if (!isPresent(kpiId)) return null;
    const source = incidentData?.source || {};
    return {
      kpiId,
      kpiName: incidentData.kpi_name,
      businessAreaId: incidentData.business_id ?? incidentData.business_area_id ?? source.business_area_id ?? null,
      subjectAreaId: incidentData.subject_id ?? incidentData.subject_area_id ?? source.subject_area_id ?? null,
      businessAreaName: incidentData.business_area || source.business_area_name || null,
      subjectAreaName: incidentData.subject_name || source.subject_area_name || null
    };
  };

  // "Go to Monitor" renders the KPI chart unless another target is given (initalAPILoad: targetType || "KPI").
  isKpiMode = () => {
    const { incidentData, targetType } = this.props;
    return isPresent(incidentData?.kpi_id) && (targetType || "KPI") === "KPI";
  };

  fetchTreeData = async (extraParams) => {
    const { teamId, userId, envId } = this.props;
    const params = {
      tenant_id: teamId,
      user_id: userId,
      env_id: envId,
      start_date: null,
      end_date: null,
      tab_type: 4,
      business_size: 10,
      ...extraParams
    };
    try {
      const response = await getLeftPanelTreeData(params);
      logModal("verticalization", { params, code: response?.code, kpiSubjectAreas: Object.keys(response?.data?.kpis || {}) });
      return response?.data || null;
    } catch (error) {
      console.error("Error fetching verticalization data:", error);
      return null;
    }
  };

  // Mirrors the realtime redirect: targeted /monitor/verticalization call (business area + subject area + KPI).
  // monitor/realtime only returns kpi_details for the requested subject area, so when the incident has no
  // subject area id it is located per business area (the unfiltered tree only lists the first business
  // area's subject areas): the KPI's entry, then the incident's subject area name, then the KPI lists.
  resolveKpiContext = async (kpi) => {
    const { envId, incidentData } = this.props;
    const cacheKey = [envId, kpi.kpiId].join("_");
    if (this.kpiContextCache[cacheKey]) return this.kpiContextCache[cacheKey];

    let { businessAreaId, subjectAreaId } = kpi;
    let kpiSource = null;
    let anyTree = false;
    const tried = new Set();
    const tryLocation = async (baId, saId) => {
      const key = `${baId}_${saId}`;
      if (tried.has(key)) return false;
      tried.add(key);
      const params = { business_type: "KPI" };
      if (isPresent(baId)) params.business_area_id = baId;
      if (isPresent(saId)) params.subject_area_id = saId;
      // Without business_type_list a subject area only returns its first business_size (10) KPIs.
      if (isPresent(baId) && isPresent(saId)) params.business_type_list = [Number(kpi.kpiId)];
      const treeData = await this.fetchTreeData(params);
      anyTree = anyTree || Boolean(treeData);
      const found = findKpiSource(treeData, kpi.kpiId, saId);
      if (!found) return false;
      kpiSource = found;
      businessAreaId = baId;
      subjectAreaId = saId;
      return true;
    };

    if (isPresent(subjectAreaId)) await tryLocation(businessAreaId, subjectAreaId);

    if (!kpiSource) {
      const fullTree = await this.fetchTreeData({});
      anyTree = anyTree || Boolean(fullTree);
      const unscanned = [];
      for (const ba of orderBusinessAreas(fullTree?.business_areas, kpi.businessAreaId, kpi.businessAreaName)) {
        const inFullTree = (fullTree?.subject_areas?.[ba.value] || []).length > 0;
        const baTree = inFullTree ? fullTree : await this.fetchTreeData({ business_area_id: ba.value, business_type: "KPI" });
        anyTree = anyTree || Boolean(baTree);
        const kpiLocation = findKpiLocation(baTree, kpi.kpiId);
        const location = kpiLocation || findSubjectAreaByName(baTree, kpi.subjectAreaName, kpi.businessAreaName);
        if (location) {
          const locationBusinessAreaId = location.businessAreaId ?? Number(ba.value);
          if (await tryLocation(locationBusinessAreaId, location.subjectAreaId)) break;
          if (kpiLocation) {
            kpiSource = findKpiSource(baTree, kpi.kpiId, kpiLocation.subjectAreaId);
            businessAreaId = locationBusinessAreaId;
            subjectAreaId = kpiLocation.subjectAreaId;
            break;
          }
        }
        unscanned.push(
          ...listSubjectAreas(baTree).filter(
            (sa) => sa.businessAreaId === Number(ba.value) && sa.subjectAreaId !== location?.subjectAreaId
          )
        );
      }
      if (!kpiSource) {
        for (const sa of unscanned.slice(0, MAX_SUBJECT_AREA_SCAN)) {
          if (await tryLocation(sa.businessAreaId, sa.subjectAreaId)) break;
        }
      }
    }

    const context = { ...getKpiDatasetContext(kpiSource), businessAreaId, subjectAreaId };
    const source = incidentData?.source || {};
    if (!isPresent(context.integrationId)) context.integrationId = source.integration_id ?? null;
    if (!isPresent(context.datasetId)) {
      context.datasetId = source.data_set_id ?? null;
      context.attributeIds = getIncidentAttributeIds(incidentData);
    }
    logModal("KPI context", {
      kpiId: kpi.kpiId,
      foundInVerticalization: context.found,
      businessAreaId,
      subjectAreaId,
      integrationId: context.integrationId,
      datasetId: context.datasetId,
      attributeIds: context.attributeIds
    });
    if (anyTree) this.kpiContextCache[cacheKey] = context;
    return context;
  };

  // The realtime redirect also needs the business/subject area ids to request the KPI's data.
  withResolvedAreaIds = (incident) => {
    const kpiId = incident?.kpi_id;
    const context = isPresent(kpiId) ? this.kpiContextCache[[this.props.envId, kpiId].join("_")] : null;
    if (!context) return incident;
    return {
      ...incident,
      business_id: isPresent(incident.business_id) ? incident.business_id : context.businessAreaId,
      subject_id: isPresent(incident.subject_id) ? incident.subject_id : context.subjectAreaId
    };
  };

  buildChartRequest = (context) => {
    const { incidentData, teamId, userId, envId } = this.props;
    const source = incidentData?.source || {};
    const datasetId = source.data_set_id;
    const attributeId = source.attribute_id;
    if (!isPresent(datasetId) || !isPresent(source.integration_id)) return null;

    const { startDate, endDate } = getTimeRangeDates(this.state.selectedTimeRange);
    const params = {
      tab_type: 1,
      tenant_id: teamId,
      env_id: envId,
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
      integration_id: source.integration_id,
      data_set_id: Number(datasetId),
      menu_type: "monitor",
      ids_type: "dataset",
      selected_period: "default"
    };
    if (isPresent(attributeId)) {
      return {
        ...params,
        type: "Attribute",
        meta_data_id: [attributeId],
        selected_ids: [attributeId]
      };
    }
    const treeAttributeIds = context ? getKpiAttributeIds(context.kpiSource, datasetId) : [];
    return {
      ...params,
      type: "DatasetandAttribute",
      meta_data_id: (treeAttributeIds.length ? treeAttributeIds : getIncidentAttributeIds(incidentData)).slice(0, 5),
      selected_ids: [Number(datasetId)],
      additional_info: { id: Number(datasetId), type: "dataset" },
      business_area_id: context?.businessAreaId ?? incidentData?.business_id,
      subject_area_id: context?.subjectAreaId ?? incidentData?.subject_id,
      business_type: incidentData?.kpi_id ? "KPI" : "MODEl",
      business_id: incidentData?.kpi_id
    };
  };

  buildKpiChartRequest = (kpi, context) => {
    const { teamId, userId, envId } = this.props;
    const datasetId = Number(context.datasetId);
    const { startDate, endDate } = getTimeRangeDates(this.state.selectedTimeRange);
    return {
      tab_type: 1,
      tenant_id: teamId,
      env_id: envId,
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
      integration_id: context.integrationId,
      data_set_id: datasetId,
      meta_data_id: context.attributeIds.slice(0, 5),
      menu_type: "monitor",
      additional_info: { id: datasetId, type: "dataset" },
      selected_ids: [datasetId],
      type: "DatasetandAttribute",
      ids_type: "dataset",
      business_area_id: context.businessAreaId,
      subject_area_id: context.subjectAreaId,
      business_type: "KPI",
      business_id: kpi.kpiId,
      selected_period: "default"
    };
  };

  fetchChart = async (chartParams) => {
    const requestParams = {
      browser_tab_id: readStoredId("localStorage", "browserTabId"),
      session_id: readStoredId("sessionStorage", "browserSessionId"),
      ...chartParams
    };
    let response;
    try {
      response = await getChartData(requestParams);
    } catch (error) {
      console.error("Error fetching monitor chart data:", error);
    }
    const chartData = response?.response?.chart_data || response?.response?.chart_details || {};
    logModal("monitor/realtime", {
      params: requestParams,
      code: response?.code,
      message: response?.message,
      chartKeys: Object.keys(chartData),
      kpiDetailKeys: Object.keys(response?.response?.kpi_details || {})
    });
    return response;
  };

  extractSeries = (responseBody, kpiMode, kpi) => {
    if (kpiMode) return getModalKpiSeries(responseBody, kpi.kpiId, kpi.kpiName);
    const { incidentData } = this.props;
    const source = incidentData?.source || {};
    const attributeNames = incidentData?.attribute_names;
    const series = getModalChartSeries(
      responseBody.chart_data || responseBody.chart_details || {},
      source.data_set_id,
      source.attribute_id,
      attributeNames && typeof attributeNames === "object" && !Array.isArray(attributeNames) ? attributeNames : {}
    );
    return series.length || !kpi ? series : getModalKpiSeries(responseBody, kpi.kpiId, kpi.kpiName);
  };

  loadChartData = async () => {
    const requestId = ++this.chartRequestId;
    const kpi = this.getKpiIds();
    const kpiMode = this.isKpiMode();
    const source = this.props.incidentData?.source || {};
    this.setState({ chartLoading: true, chartError: null, chartDiagnostics: null });

    // Dataset charts also take their attribute ids from the KPI's verticalization entry, as on the realtime page.
    const needsContext = kpi && (kpiMode || !isPresent(source.attribute_id));
    const context = needsContext ? await this.resolveKpiContext(kpi) : null;
    if (requestId !== this.chartRequestId) return;

    let requestParams = null;
    if (kpiMode) {
      if (isPresent(context.integrationId) && isPresent(context.datasetId)) {
        requestParams = this.buildKpiChartRequest(kpi, context);
      }
    } else {
      requestParams = this.buildChartRequest(context);
    }
    let chartSeries = [];
    let failure = null;
    let diagnostics = null;
    if (!requestParams) {
      logModal("monitor request skipped", { incident: this.props.incidentData, targetType: this.props.targetType });
      diagnostics = kpi
        ? `KPI ${kpi.kpiId} has no integration/dataset in /monitor/verticalization (subject area ${context?.subjectAreaId ?? "-"}, env ${this.props.envId ?? "-"})`
        : "Incident has no KPI, integration or dataset id";
    } else {
      const response = await this.fetchChart(requestParams);
      if (requestId !== this.chartRequestId) return;

      if (!response || !response.response || response.code === 401) {
        failure = response?.message || CHART_FAILED;
      } else {
        chartSeries = this.extractSeries(response.response, kpiMode, kpi);

        // "Go to Monitor" opens the realtime page on the KPI chart, so fall back to the KPI when this view is empty.
        if (!chartSeries.length && !kpiMode && kpi) {
          const kpiContext = context || (await this.resolveKpiContext(kpi));
          if (requestId !== this.chartRequestId) return;
          if (isPresent(kpiContext.integrationId) && isPresent(kpiContext.datasetId)) {
            const kpiResponse = await this.fetchChart(this.buildKpiChartRequest(kpi, kpiContext));
            if (requestId !== this.chartRequestId) return;
            chartSeries = getModalKpiSeries(kpiResponse?.response, kpi.kpiId, kpi.kpiName);
          }
        }
      }

      let lastParams = requestParams;
      let lastResponse = response;

      // kpi_store rows are filtered by computed_time, so a KPI computed less often than the 30-day default
      // window comes back empty; retry over the dataset's history (the backend keeps the latest 30 points).
      // Dates are required: without them monitor/realtime returns no response when the dataset has no refresh details.
      if (!chartSeries.length && !this.state.selectedTimeRange) {
        lastParams = { ...requestParams, start_date: getHistoryStartDate(response?.response) };
        lastResponse = await this.fetchChart(lastParams);
        if (requestId !== this.chartRequestId) return;
        if (lastResponse?.response) {
          chartSeries = this.extractSeries(lastResponse.response, kpiMode, kpi);
          if (chartSeries.length) failure = null;
        }
      }
      diagnostics = describeChartAttempt(lastParams, lastResponse, kpi);
    }

    // Realtime's range buttons build the KPI chart from `responseKpis[id] || rawKpi`, i.e. they keep the KPI
    // series already loaded when the new range has no rows for it.
    if (!chartSeries.length && kpiMode && requestParams && this.state.selectedTimeRange) {
      chartSeries = this.state.chartSeries;
    }

    this.setState({
      chartWindow: requestParams ? { startDate: requestParams.start_date, endDate: requestParams.end_date } : null,
      chartLoading: false,
      chartError: chartSeries.length ? null : failure || (requestParams ? CHART_EMPTY : CHART_UNAVAILABLE),
      chartDiagnostics: chartSeries.length ? null : diagnostics,
      chartSeries
    });
  };

  handleTimeRangeChange = (range) => {
    this.setState({ selectedTimeRange: range }, this.loadChartData);
  };

  handleLikeToggle = (type) => {
    this.setState((prev) => ({
      liked: prev.liked === type ? null : type
    }));
  };

  render() {
    const {
      isOpen,
      onClose,
      onOpenFullMonitor,
      incidentData,
      theme,
      chartAreaBg,
      aiInsightsBg,
      recommendBg,
      aiInsightsBorderBg,
      modalBg,
      modalBorder,
      backdropBg
    } = this.props;

    if (!isOpen) return null;

    const isBlue = theme === "blue" || chartAreaBg === "#071933";

    const mainSource = incidentData?.source?.env_name || "Main Source";
    const integrationName = incidentData?.source?.integration_name || "Retail Transaction";
    const datasetName = incidentData?.source?.data_set_name || "retail_transactions.csv";
    const kpiTitle = this.isKpiMode() ? incidentData?.kpi_name : null;
    const attributeName = kpiTitle || incidentData?.source?.attribute_name || incidentData?.attribute_name || "total_revenue";
    const profiledTime = incidentData?.incident_time || "Sun, 07 Sep 2025 08:15:04 GMT";

    const { chartLoading, chartError, chartDiagnostics, chartSeries, chartWindow, selectedSeriesKey, selectedChartType } =
      this.state;
    const activeSeries = chartSeries.find((series) => series.key === selectedSeriesKey) || chartSeries[0] || null;
    const kpiMode = this.isKpiMode();
    const { insights, insightsLoading, insightsError } = this.state;
    const hasInsights = Boolean(insights && (insights.title || insights.paragraphs?.length || insights.recommendations?.length));
    const isClassBg = chartAreaBg && (chartAreaBg.startsWith("tw-") || chartAreaBg.startsWith("!tw-"));
    const chartBg = chartAreaBg && !isClassBg ? chartAreaBg : isBlue ? "#071933" : "#0A0824";

    return (
      <div
        className="tw-fixed tw-inset-0 tw-z-[9999] tw-backdrop-blur-[10px] tw-flex tw-items-center tw-justify-center tw-p-[50px_24px] tw-overflow-y-auto"
        style={{
          background: backdropBg || (isBlue ? "rgba(1, 10, 49, 0.85)" : "rgba(9, 5, 32, 0.75)")
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && onClose) {
            onClose();
          }
        }}
      >
        <style>{`
          @keyframes modalPopFromCenter {
            0% {
              opacity: 0;
              transform: scale(0.90);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>

        {/* Main Modal Window */}
        <div
          className={`stw-w-full tw-max-w-[1180px] tw-h-[calc(100vh-100px)] tw-flex tw-flex-col tw-rounded-[24px] ${
            isBlue ? "tw-shadow-[0_40px_100px_rgba(1,5,25,0.85)]" : "tw-shadow-[0_40px_100px_rgba(4,1,20,0.75)]"
          } tw-overflow-hidden tw-text-white ${
            modalBg || (isBlue ? "tw-bg-[#08163A]" : "tw-bg-[#14113A]/65")
          }`}
          style={{
            border: modalBorder || (isBlue ? "1px solid rgba(68, 110, 255, 0.3)" : "1px solid rgba(98, 67, 255, 0.3)"),
            animation: "modalPopFromCenter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards"
          }}
        >
          {/* Header */}
          <div className="tw-flex-none tw-flex tw-items-center tw-gap-[14px] tw-p-5 tw-pb-0">
            <h2 className="tw-m-0 tw-text-2xl tw-font-semibold tw-flex tw-items-center tw-gap-2 tw-text-[#CCCCCC]">
              <div className="tw-flex tw-items-center tw-justify-center">
                <svg width="32" height="32" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.7695 3.84375C15.1483 3.84383 15.469 3.97488 15.7314 4.2373C15.9939 4.49973 16.1249 4.82049 16.125 5.19922V13.7383C16.1249 14.117 15.9939 14.4378 15.7314 14.7002C15.469 14.9626 15.1483 15.0937 14.7695 15.0938H3.23047C2.85174 15.0937 2.53098 14.9626 2.26855 14.7002C2.00613 14.4378 1.87508 14.117 1.875 13.7383V5.19922C1.87508 4.82049 2.00613 4.49973 2.26855 4.2373C2.53098 3.97488 2.85174 3.84383 3.23047 3.84375H14.7695ZM4.5 7.21875V11.2969H5.49316V9.49609L6.86426 11.2969H8.0791L6.44043 9.21777L8.02051 7.21875H6.85254L5.49316 9.00781V7.21875H4.5ZM8.55078 11.2969H9.54395V9.83301H10.1602C11.1999 9.83301 11.659 9.20601 11.6592 8.53223C11.6592 7.78275 11.1595 7.21875 10.1602 7.21875H8.55078V11.2969ZM12.1768 11.2969H13.1699V7.21875H12.1768V11.2969ZM10.085 8.02051C10.4799 8.02058 10.6484 8.21855 10.6484 8.53223C10.6483 8.84565 10.4797 9.04289 10.085 9.04297H9.54395V8.02051H10.085Z" fill="#cccccc"></path></svg>
              </div>
              <span>
                {attributeName}
              </span>
            </h2>

            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-[8px]">
              <button
                type="button"
                onClick={() => {
                  if (onOpenFullMonitor) onOpenFullMonitor(this.withResolvedAreaIds(incidentData));
                  else if (onClose) onClose();
                }}
                className="tw-inline-flex tw-items-center tw-gap-[8px] tw-px-[14px] tw-py-[7px] tw-rounded-[8px] tw-text-[13px] tw-font-medium tw-text-white tw-transition-all tw-duration-200 cursor-pointer"
                style={{
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.18)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.16)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.18)";
                }}
              >
                <span>Go to Monitor</span>
                <ExternalLink className="tw-w-[14px] tw-h-[14px]" />
              </button>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="tw-w-[32px] tw-h-[32px] tw-rounded-[9px] tw-flex tw-items-center tw-justify-center tw-text-white/60 hover:tw-text-white hover:tw-bg-white/10 tw-border-0 tw-bg-transparent tw-transition-colors cursor-pointer"
              >
                <X className="tw-w-[18px] tw-h-[18px]" />
              </button>
            </div>
          </div>

          {/* Breadcrumbs */}
          <div className="tw-flex-none tw-flex tw-items-center tw-gap-[8px] tw-flex-wrap tw-px-5 tw-pt-3 tw-text-sm tw-text-white/60">
            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Database className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(mainSource)}
            </span>
            <ChevronRight className="tw-w-[12px] tw-h-[12px] tw-opacity-45" />

            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Blocks className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(integrationName)}
            </span>
            <ChevronRight className="tw-w-[12px] tw-h-[12px] tw-opacity-45" />

            <span className="tw-inline-flex tw-items-center tw-gap-[7px]">
              <Layers className="tw-w-[14px] tw-h-[14px]" />
              {renderBreadcrumbText(datasetName)}
            </span>

            <span className="tw-ml-auto tw-text-white/45 tw-inline-flex tw-items-center tw-gap-[7px]">
              <Clock className="tw-w-[14px] tw-h-[14px]" />
              <span>
                Last profiled at <b className="tw-text-white/80 tw-font-medium">{profiledTime}</b>
              </span>
            </span>
          </div>

          {/* Scrollable Body */}
          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-y-auto">
            {/* Chart Card Panel */}
            <div
              className={`tw-flex-1 tw-min-h-[280px] tw-mx-5 tw-mt-[14px] tw-mb-0 tw-rounded-[12px] tw-p-4 tw-flex tw-flex-col ${
                chartAreaBg && (chartAreaBg.startsWith("tw-") || chartAreaBg.startsWith("!tw-"))
                  ? chartAreaBg
                  : ""
              }`}
              style={{
                background:
                  !chartAreaBg || chartAreaBg.startsWith("tw-") || chartAreaBg.startsWith("!tw-")
                    ? (chartAreaBg?.startsWith("tw-") || chartAreaBg?.startsWith("!tw-") ? undefined : (isBlue ? "#071933" : "#0A0824"))
                    : chartAreaBg,
                border:
                  isBlue || chartAreaBg === "#071933"
                    ? "1px solid rgba(255, 255, 255, 0.12)"
                    : "1px solid rgba(255, 255, 255, 0.1)",
                // Grow to fit the series switcher + chart so the chart never spills over the range buttons.
                flex: "1 0 auto"
              }}
            >
              {chartSeries.length > 1 && (
                <div className="tw-flex-none tw-flex tw-items-center tw-gap-[4px] tw-flex-wrap tw-mb-[8px]">
                  {chartSeries.map((series) => {
                    const isSelected = activeSeries && series.key === activeSeries.key;
                    return (
                      <button
                        key={series.key}
                        type="button"
                        onClick={() => this.setState({ selectedSeriesKey: series.key })}
                        className="tw-px-[8px] tw-py-[3px] tw-rounded-[6px] tw-text-xs tw-font-medium tw-border-0 tw-transition-colors cursor-pointer"
                        style={{
                          background: isSelected ? "rgba(255, 255, 255, 0.16)" : "transparent",
                          color: isSelected ? "#ffffff" : "rgba(255, 255, 255, 0.6)"
                        }}
                      >
                        {series.name}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* The realtime Vertical page's chart widget (renderChartCell), fed the same series */}
              <div
                className="tw-relative tw-w-full tw-flex tw-items-center tw-justify-center"
                style={{ flex: "1 0 340px", minHeight: 340 }}
              >
                {chartLoading && !activeSeries ? (
                  <span className="tw-text-sm tw-text-white/60">Loading chart...</span>
                ) : (chartError && !chartLoading) || !activeSeries ? (
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-[6px] tw-text-center tw-px-4">
                    <span className="tw-text-sm tw-text-white/60">{chartError || CHART_EMPTY}</span>
                    {chartDiagnostics && (
                      <span className="tw-text-[11px] tw-text-white/40 tw-break-all">{chartDiagnostics}</span>
                    )}
                  </div>
                ) : (
                  <div
                    className="qd-rt_chart-container"
                    style={{
                      ...getDarkChartVars(chartBg),
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      opacity: chartLoading ? 0.5 : 1
                    }}
                  >
                    {/* renderChartCell's cell: the flex container gives the widget no width without it */}
                    <div style={{ flex: 1, minWidth: 0, height: "100%", boxSizing: "border-box" }}>
                      <Suspense
                        fallback={
                          <div className="qd-rt_chart-widget" style={{ height: "100%" }}>
                            <Load />
                          </div>
                        }
                      >
                        <div className="qd-rt_chart-widget" style={{ height: "100%" }}>
                          <ChartComponent
                            tab="verticalization"
                            key={`${activeSeries.key}-${selectedChartType}`}
                            chartType={selectedChartType}
                            onChartTypeChange={this.handleChartTypeChange}
                            chartIndex={0}
                            chartColIndex={0}
                            lastProcessedDate={activeSeries.raw.last_processed_time}
                            selectedDatasetName={datasetName}
                            selectedDatasetAttrName={kpiMode ? "" : incidentData?.source?.attribute_name}
                            selectedKpiId={kpiMode ? incidentData.kpi_id : undefined}
                            selectedKpiName={kpiMode ? incidentData.kpi_name : undefined}
                            realdata={activeSeries.raw}
                            dataType={kpiMode ? "KPI" : isPresent(incidentData?.source?.attribute_id) ? "Attribute" : "Dataset"}
                            timeGranularitySelected="default"
                            theme="dark"
                            darkMode
                            chartBackground={chartBg}
                          />
                        </div>
                      </Suspense>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Time Ranges */}
            <div
              className="tw-flex-none tw-flex tw-items-center tw-gap-[4px] tw-px-5 tw-pt-2 tw-pb-0"
              style={{ position: "relative", zIndex: 2 }}
            >
              {TIME_RANGES.map((range) => {
                const isActive = this.state.selectedTimeRange === range;
                return (
                  <button
                    key={range}
                    type="button"
                    onClick={() => this.handleTimeRangeChange(range)}
                    className="tw-min-w-[40px] tw-px-[12px] tw-py-[6px] tw-rounded-[8px] tw-text-xs tw-font-medium tw-border-0 tw-transition-colors cursor-pointer"
                    style={{
                      background: isActive ? "rgba(255, 255, 255, 0.16)" : "transparent",
                      color: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.6)"
                    }}
                  >
                    {range}
                  </button>
                );
              })}
              {chartWindow && chartWindow.endDate && (
                <span className="tw-ml-[8px] tw-text-xs tw-text-white/50 tw-whitespace-nowrap">
                  {this.state.selectedTimeRange === TIME_RANGES[0]
                    ? `${moment(chartWindow.startDate).format("DD-MM-YYYY")} (GMT)`
                    : `${moment(chartWindow.startDate).format("DD-MMM-YYYY")} - ${moment(chartWindow.endDate).format("DD-MMM-YYYY")} (GMT)`}
                </span>
              )}
            </div>

            {/* Qualdo AI Insights Card */}
            <div
              className="tw-flex-none tw-m-5 tw-p-[1px] tw-rounded-[12px]"
              style={{
                background:
                  aiInsightsBorderBg ||
                  (isBlue
                    ? "linear-gradient(45deg, #446EFF 15%, #2E96FF 40%, #B1C5FF 73%)"
                    : "rgba(98, 67, 255, 0.3)")
              }}
            >
              <div
                style={
                  !aiInsightsBg ||
                  (!aiInsightsBg.startsWith("tw-") && !aiInsightsBg.startsWith("!tw-"))
                    ? {
                        background:
                          aiInsightsBg ||
                          (isBlue
                            ? "linear-gradient(160deg, #383082, #261e5f)"
                            : "linear-gradient(135deg, #250E3C -85%, #280B3D 78%)")
                      }
                    : undefined
                }
                className={`tw-rounded-[12px] tw-p-4 ${
                  aiInsightsBg &&
                  (aiInsightsBg.startsWith("tw-") || aiInsightsBg.startsWith("!tw-"))
                    ? aiInsightsBg
                    : (isBlue && !aiInsightsBg ? "!tw-bg-[linear-gradient(160deg,#383082,#261e5f)]" : "")
                }`}
              >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <g clipPath="url(#clip0_31369_201361)">
                        <path
                          d="M16.6665 1.66667V5M18.3332 3.33333H14.9999M9.18071 2.345C9.21642 2.15384 9.31786 1.98118 9.46746 1.85693C9.61706 1.73269 9.80541 1.66467 9.99988 1.66467C10.1943 1.66467 10.3827 1.73269 10.5323 1.85693C10.6819 1.98118 10.7833 2.15384 10.819 2.345L11.6949 6.97667C11.7571 7.30596 11.9171 7.60885 12.1541 7.84581C12.391 8.08277 12.6939 8.2428 13.0232 8.305L17.6549 9.18084C17.846 9.21654 18.0187 9.31798 18.1429 9.46758C18.2672 9.61719 18.3352 9.80553 18.3352 10C18.3352 10.1945 18.2672 10.3828 18.1429 10.5324C18.0187 10.682 17.846 10.7835 17.6549 10.8192L13.0232 11.695C12.6939 11.7572 12.391 11.9172 12.1541 12.1542C11.9171 12.3912 11.7571 12.694 11.6949 13.0233L10.819 17.655C10.7833 17.8462 10.6819 18.0188 10.5323 18.1431C10.3827 18.2673 10.1943 18.3353 9.99988 18.3353C9.80541 18.3353 9.61706 18.2673 9.46746 18.1431C9.31786 18.0188 9.21642 17.8462 9.18071 17.655L8.30488 13.0233C8.24267 12.694 8.08265 12.3912 7.84569 12.1542C7.60873 11.9172 7.30584 11.7572 6.97655 11.695L2.34488 10.8192C2.15372 10.7835 1.98106 10.682 1.85681 10.5324C1.73256 10.3828 1.66455 10.1945 1.66455 10C1.66455 9.80553 1.73256 9.61719 1.85681 9.46758C1.98106 9.31798 2.15372 9.21654 2.34488 9.18084L6.97655 8.305C7.30584 8.2428 7.60873 8.08277 7.84569 7.84581C8.08265 7.60885 8.24267 7.30596 8.30488 6.97667L9.18071 2.345ZM4.99988 16.6667C4.99988 17.5871 4.25369 18.3333 3.33321 18.3333C2.41274 18.3333 1.66655 17.5871 1.66655 16.6667C1.66655 15.7462 2.41274 15 3.33321 15C4.25369 15 4.99988 15.7462 4.99988 16.6667Z"
                          stroke="url(#paint0_linear_31369_201361)"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <linearGradient id="paint0_linear_31369_201361" x1="4.09548" y1="15.8724" x2="15.9216" y2="4.13483" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#446EFF" />
                          <stop offset="0.37" stopColor="#2E96FF" />
                          <stop offset="0.83" stopColor="#B1C5FF" />
                        </linearGradient>
                        <clipPath id="clip0_31369_201361">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <span className="tw-text-[18px] tw-font-semibold tw-text-white">QualdoAI Insights</span>

                    {insights?.badge && (
                      <div className="tw-bg-[#53030C] tw-border tw-border-solid tw-border-[#F64E60] tw-rounded-[6px] tw-py-[3px] tw-px-2 tw-text-xs tw-font-medium tw-text-[#F7BAC1] tw-inline-flex tw-items-center tw-gap-1 tw-leading-none">
                        <TriangleAlert className="tw-w-3 tw-h-3" />
                        <span>{insights.badge}</span>
                      </div>
                    )}
                  </div>

                  {/* Vote Buttons */}
                  <div className="tw-flex tw-items-center tw-gap-[2px] tw-text-[#94979C]">
                    <button
                      type="button"
                      onClick={() => this.handleLikeToggle("up")}
                      className="tw-p-1 tw-w-[32px] tw-h-[32px] tw-border-0 tw-bg-transparent hover:tw-text-white hover:!tw-bg-white/10 tw-rounded-full tw-transition-colors cursor-pointer"
                      style={{
                        color: this.state.liked === "up" ? "#FFFFFF" : "#94979C"
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                        {this.state.liked === "up" ? (
                          <path d="M709.23-140H313.08v-480l265.38-263.84L612.3-850q6.24 6.23 10.35 16.5 4.12 10.27 4.12 19.35V-804L584.3-620h243.39q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.31 13.46t-3.92 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140ZM253.08-620v480H100v-480h153.08Z" />
                        ) : (
                          <path d="M709.23-140H288.46v-480l265.39-263.84L587.69-850q6.23 6.23 10.35 16.5 4.11 10.27 4.11 19.35V-804l-42.46 184h268q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.62 13.46-1.61 7.23-3.61 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140Zm-360.77-60h360.77q4.23 0 8.65-2.31 4.43-2.31 6.74-7.69L840-480v-67.69q0-5.39-3.46-8.85t-8.85-3.46H483.85L534-779.23 348.46-594.46V-200Zm0-394.46V-200v-394.46Zm-60-25.54v60H160v360h128.46v60H100v-480h188.46Z" />
                        )}
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => this.handleLikeToggle("down")}
                      className="tw-p-1 tw-w-[32px] tw-h-[32px] tw-border-0 tw-bg-transparent hover:tw-text-white hover:!tw-bg-white/10 tw-rounded-full tw-transition-colors cursor-pointer"
                      style={{
                        color: this.state.liked === "down" ? "#FFFFFF" : "#94979C"
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor">
                        <g transform="rotate(180 480 -480)">
                          {this.state.liked === "down" ? (
                            <path d="M709.23-140H313.08v-480l265.38-263.84L612.3-850q6.24 6.23 10.35 16.5 4.12 10.27 4.12 19.35V-804L584.3-620h243.39q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.31 13.46t-3.92 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140ZM253.08-620v480H100v-480h153.08Z" />
                          ) : (
                            <path d="M709.23-140H288.46v-480l265.39-263.84L587.69-850q6.23 6.23 10.35 16.5 4.11 10.27 4.11 19.35V-804l-42.46 184h268q28.54 0 50.42 21.89Q900-576.23 900-547.69v64.61q0 6.23-1.62 13.46-1.61 7.23-3.61 13.47L780.15-185.69q-8.61 19.23-28.84 32.46T709.23-140Zm-360.77-60h360.77q4.23 0 8.65-2.31 4.43-2.31 6.74-7.69L840-480v-67.69q0-5.39-3.46-8.85t-8.85-3.46H483.85L534-779.23 348.46-594.46V-200Zm0-394.46V-200v-394.46Zm-60-25.54v60H160v360h128.46v60H100v-480h188.46Z" />
                          )}
                        </g>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Body Content */}
                <div className="tw-pl-[28px]">
                  {insightsLoading ? (
                    <p className="tw-text-sm tw-text-[#E2EAFE]/85 tw-mb-0 tw-leading-relaxed">Loading AI insights...</p>
                  ) : insightsError ? (
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                      <p className="tw-text-sm tw-text-[#F7BAC1] tw-mb-0 tw-leading-relaxed">{insightsError}</p>
                      <button
                        type="button"
                        onClick={this.loadInsights}
                        className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-semibold tw-underline tw-text-[#8FB0FF] cursor-pointer"
                      >
                        Retry
                      </button>
                    </div>
                  ) : !hasInsights ? (
                    <p className="tw-text-sm tw-text-[#E2EAFE]/85 tw-mb-0 tw-leading-relaxed">
                      No AI insights available for this incident yet.
                    </p>
                  ) : (
                    <>
                      {insights.title && (
                        <h4 className="tw-text-[16px] tw-font-semibold tw-text-white tw-mb-2 tw-leading-snug">{insights.title}</h4>
                      )}
                      <AiInsightPanelParagraphs paragraphs={insights.paragraphs} />
                    </>
                  )}

                  {/* Recommendation Box */}
                  {!insightsLoading && !insightsError && insights?.recommendations?.length > 0 && (
                    <div
                      className={`${
                        recommendBg || (isBlue ? "tw-bg-[#071933]/80" : "tw-bg-[#13161B7A]")
                      } ${
                        recommendBg
                          ? (recommendBg.includes("tw-border")
                              ? ""
                              : "tw-border tw-border-solid tw-border-[#24344D]")
                          : (isBlue ? "tw-border tw-border-solid tw-border-[#24344D]" : "tw-border tw-border-solid tw-border-[#22262F]")
                      } tw-rounded-[12px] tw-py-3 tw-px-4 tw-mt-3`}
                    >
                      <AiInsightPanelRecommendation paragraphs={insights.recommendations} spaced={false} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default verticalDashboardModal;
